import { useState, useRef, useCallback, useEffect } from "react";
import { NavLink, useLocation } from "react-router-dom";
import {
  LayoutDashboard,
  DollarSign,
  ListChecks,
  Users,
  Home,
  ShieldCheck,
  LogOut,
  X,
  Settings as SettingsIcon,
  Image as ImageIcon,
  BookOpen,
  Map,
  MessageSquare,
  FolderOpen,
  Plus,
  Check,
  Receipt,
  ShoppingCart,
} from "lucide-react";
import { useTheme } from "@contexts/ThemeContext";
import { useProjects } from "@contexts/ProjectContext";
import { roundSqFt } from "@domain/project/projectData";
import { planBTotalSqFt } from "@domain/budget/planB";

// `parent` marks a collapsible section header; `sub` marks its children.
const navItems = [
  { to: "/", icon: LayoutDashboard, label: "Dashboard" },
  { to: "/timeline", icon: ListChecks, label: "Timeline" },
  { to: "/budget", icon: DollarSign, label: "Plan" },
  { to: "/invoices", icon: Receipt, label: "Invoices" },
  { to: "/shopping", icon: ShoppingCart, label: "Shopping List" },
  { to: "/design", icon: ImageIcon, label: "Design", parent: true },
  { to: "/design", icon: BookOpen, label: "Design Book", sub: true, exact: true },
  { to: "/floorplans", icon: Map, label: "Floorplans", sub: true },
  { to: "/feedback", icon: MessageSquare, label: "Feedback" },
  { to: "/permits", icon: ShieldCheck, label: "Permits" },
  { to: "/contractors", icon: Users, label: "Contractors" },
  { to: "/settings", icon: SettingsIcon, label: "Settings" },
];

// Per-theme inline color values (CSS variables can't be used in JSX expressions
// that need string interpolation for borders/gradients, so we keep a small map).
const themeColors = {
  light: {
    isLight: true,
    border: "rgba(79,55,51,0.10)",
    borderSoft: "rgba(79,55,51,0.08)",
    headingText: "#2D1F1B",
    secondaryText: "#8B7B72",
    bodyText: "#6B5750",
    accentText: "#B07518",
    accentBg: "rgba(176,117,24,0.14)",
    hoverBg: "rgba(79,55,51,0.06)",
    sidebarGradient: "linear-gradient(180deg, #FAF6EA 0%, #EFE9D7 100%)",
    sidebarBorder: "rgba(79,55,51,0.10)",
  },
  dark: {
    isLight: false,
    border: "rgba(255,255,255,0.08)",
    borderSoft: "rgba(255,255,255,0.06)",
    headingText: "#e5e7eb",
    secondaryText: "#6b7280",
    bodyText: "#9ca3af",
    accentText: "#fbbf24",
    accentBg: "rgba(245, 158, 11, 0.15)",
    hoverBg: "rgba(255,255,255,0.04)",
    sidebarGradient: "linear-gradient(180deg, rgba(15, 17, 30, 1) 0%, rgba(12, 14, 25, 1) 100%)",
    sidebarBorder: "rgba(255,255,255,0.08)",
  },
  midcentury: {
    isLight: false,
    border: "rgba(244,241,234,0.10)",
    borderSoft: "rgba(244,241,234,0.06)",
    headingText: "#F4F1EA",
    secondaryText: "#A8A095",
    bodyText: "#DCD7CB",
    accentText: "#C48D3B",
    accentBg: "rgba(196,141,59,0.18)",
    hoverBg: "rgba(244,241,234,0.05)",
    sidebarGradient: "linear-gradient(180deg, #323A44 0%, #262D36 100%)",
    sidebarBorder: "rgba(244,241,234,0.10)",
  },
  highcontrast: {
    isLight: false,
    border: "rgba(255,255,255,0.22)",
    borderSoft: "rgba(255,255,255,0.14)",
    headingText: "#FFFFFF",
    secondaryText: "#A0A0A0",
    bodyText: "#D0D0D0",
    accentText: "#FBBF24",
    accentBg: "rgba(251,191,36,0.20)",
    hoverBg: "rgba(255,255,255,0.08)",
    sidebarGradient: "linear-gradient(180deg, #0A0A0A 0%, #000000 100%)",
    sidebarBorder: "rgba(255,255,255,0.22)",
  },
  "highcontrast-light": {
    isLight: true,
    border: "rgba(0,0,0,0.18)",
    borderSoft: "rgba(0,0,0,0.10)",
    headingText: "#000000",
    secondaryText: "#555555",
    bodyText: "#333333",
    accentText: "#1D4ED8",
    accentBg: "rgba(29,78,216,0.14)",
    hoverBg: "rgba(0,0,0,0.05)",
    sidebarGradient: "linear-gradient(180deg, #FAFAFA 0%, #F5F5F5 100%)",
    sidebarBorder: "rgba(0,0,0,0.18)",
  },
  earthen: {
    isLight: true,
    border: "rgba(60,42,34,0.11)",
    borderSoft: "rgba(60,42,34,0.08)",
    headingText: "#3D2A22",
    secondaryText: "#998875",
    bodyText: "#7A6A5E",
    accentText: "#7A2E27",
    accentBg: "rgba(122,46,39,0.13)",
    hoverBg: "rgba(60,42,34,0.06)",
    sidebarGradient: "linear-gradient(180deg, #F5ECD9 0%, #E8DDC4 100%)",
    sidebarBorder: "rgba(60,42,34,0.11)",
  },
};

export default function Sidebar({ onSignOut, userEmail, customThemes = [], mobileOpen = false, setMobileOpen }) {
  const { pathname } = useLocation();
  const { theme } = useTheme();
  const { projects, setActiveProject, createProject, activeProject } = useProjects();
  const activeKey = activeProject
    ? `${activeProject.ownerUid}|${activeProject.id}`
    : null;
  const activeCustom =
    typeof theme === "string" && theme.startsWith("custom:")
      ? customThemes.find((t) => `custom:${t.id}` === theme)
      : null;
  const c =
    activeCustom?.sidebarColors || themeColors[theme] || themeColors.dark;

  // ── Draggable sidebar width ──
  const [sidebarWidth, setSidebarWidth] = useState(() => {
    const saved = localStorage.getItem("reno-sidebar-width");
    return saved ? parseInt(saved, 10) : 256; // default w-64 = 256px
  });
  const dragging = useRef(false);
  const startX = useRef(0);
  const startW = useRef(256);

  const onDragStart = useCallback((e) => {
    e.preventDefault();
    dragging.current = true;
    startX.current = e.clientX || e.touches?.[0]?.clientX || 0;
    startW.current = sidebarWidth;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
  }, [sidebarWidth]);

  useEffect(() => {
    const onMove = (e) => {
      if (!dragging.current) return;
      const x = e.clientX || e.touches?.[0]?.clientX || 0;
      const newW = Math.max(180, Math.min(480, startW.current + x - startX.current));
      setSidebarWidth(newW);
    };
    const onUp = () => {
      if (!dragging.current) return;
      dragging.current = false;
      document.body.style.cursor = "";
      document.body.style.userSelect = "";
      localStorage.setItem("reno-sidebar-width", String(sidebarWidth));
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchmove", onMove);
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("touchend", onUp);
    };
  }, [sidebarWidth]);

  const sidebarContent = (
    <>
      <div className="px-4 pt-4 pb-2 pr-12 flex items-center">
        <img
          src="/pb_logo.png"
          alt="Posh Block"
          className="w-10 h-10 flex-shrink-0 object-contain"
          style={
            c.isLight
              ? { mixBlendMode: "multiply" }
              : { filter: "invert(1)" }
          }
        />
      </div>
      <div
        className="px-4 pb-4 pr-12"
        style={{ borderBottom: `1px solid ${c.border}` }}
      >
        <div className="flex items-center gap-2.5">
          <div
            className="p-1.5 rounded-md flex-shrink-0"
            style={{ color: c.accentText, background: c.accentBg }}
          >
            <Home className="w-4 h-4" />
          </div>
          <div className="min-w-0">
            <h2
              className="font-semibold text-sm leading-tight truncate"
              style={{ color: c.headingText }}
            >
              {activeProject?.name || "Untitled Project"}
            </h2>
            <p className="text-[10px] truncate" style={{ color: c.secondaryText }}>
              {activeProject?.address || ""}
            </p>
          </div>
        </div>
      </div>
      <nav className="p-3">
        {navItems.map(({ to, icon: Icon, label, parent, sub, exact }) => {
          const isDesignSection = parent && (pathname === "/design" || pathname === "/floorplans");
          if (parent) {
            return (
              <div key={`${to}-parent`}>
                <NavLink
                  to={to}
                  end
                  onClick={() => setMobileOpen?.(false)}
                  className="flex items-center gap-3 px-3 py-2.5 rounded-lg mb-0.5 transition-colors"
                  style={() =>
                    isDesignSection
                      ? { background: c.accentBg, color: c.accentText }
                      : { color: c.bodyText }
                  }
                  onMouseEnter={(e) => {
                    if (!isDesignSection) {
                      e.currentTarget.style.background = c.hoverBg;
                      e.currentTarget.style.color = c.headingText;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!isDesignSection) {
                      e.currentTarget.style.background = "transparent";
                      e.currentTarget.style.color = c.bodyText;
                    }
                  }}
                >
                  <Icon className="w-4 h-4" />
                  <span className="text-xs font-medium tracking-wide">{label}</span>
                </NavLink>
              </div>
            );
          }
          if (sub) {
            return (
              <NavLink
                key={`${to}-${label}`}
                to={to}
                end={exact}
                onClick={() => setMobileOpen?.(false)}
                className="flex items-center gap-3 pl-9 pr-3 py-1.5 rounded-lg mb-0.5 transition-colors"
                style={({ isActive }) =>
                  isActive
                    ? { color: c.accentText }
                    : { color: c.secondaryText }
                }
                onMouseEnter={(e) => {
                  if (e.currentTarget.getAttribute("aria-current") !== "page") {
                    e.currentTarget.style.background = c.hoverBg;
                    e.currentTarget.style.color = c.headingText;
                  }
                }}
                onMouseLeave={(e) => {
                  if (e.currentTarget.getAttribute("aria-current") !== "page") {
                    e.currentTarget.style.background = "transparent";
                    e.currentTarget.style.color = c.secondaryText;
                  }
                }}
              >
                <Icon className="w-3.5 h-3.5" />
                <span className="text-[11px] font-medium tracking-wide">{label}</span>
              </NavLink>
            );
          }
          return (
            <NavLink
              key={to}
              to={to}
              end={to === "/"}
              onClick={() => setMobileOpen?.(false)}
              className="flex items-center gap-3 px-3 py-2.5 rounded-lg mb-1 transition-colors"
              style={({ isActive }) =>
                isActive
                  ? { background: c.accentBg, color: c.accentText }
                  : { color: c.bodyText }
              }
              onMouseEnter={(e) => {
                const active = e.currentTarget.getAttribute("aria-current") === "page";
                if (!active) {
                  e.currentTarget.style.background = c.hoverBg;
                  e.currentTarget.style.color = c.headingText;
                }
              }}
              onMouseLeave={(e) => {
                const active = e.currentTarget.getAttribute("aria-current") === "page";
                if (!active) {
                  e.currentTarget.style.background = "transparent";
                  e.currentTarget.style.color = c.bodyText;
                }
              }}
            >
              <Icon className="w-4 h-4" />
              <span className="text-xs font-medium tracking-wide">{label}</span>
            </NavLink>
          );
        })}
      </nav>

      {/* My Projects — right below nav */}
      <div className="px-3 pb-3">
        <div
          className="text-[10px] uppercase tracking-wider font-semibold px-3 mb-2"
          style={{ color: c.secondaryText }}
        >
          My Projects
        </div>
        <div className="space-y-0.5 max-h-40 overflow-y-auto">
          {projects.map((p) => {
            const pKey = `${p.ownerUid}|${p.id}`;
            const isActive = pKey === activeKey;
            return (
              <button
                key={pKey}
                onClick={() => setActiveProject(p)}
                className="w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-left transition-colors"
                style={
                  isActive
                    ? { background: c.accentBg, color: c.accentText }
                    : { color: c.bodyText }
                }
                onMouseEnter={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.background = c.hoverBg;
                    e.currentTarget.style.color = c.headingText;
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isActive) {
                    e.currentTarget.style.background = "transparent";
                    e.currentTarget.style.color = c.bodyText;
                  }
                }}
                title={p.shared ? `Shared by ${p.ownerEmail}` : p.name}
              >
                {isActive ? (
                  <Check className="w-4 h-4 flex-shrink-0" />
                ) : (
                  <FolderOpen className="w-4 h-4 flex-shrink-0" />
                )}
                <span className="text-xs font-medium tracking-wide truncate flex-1 min-w-0">
                  {p.name}
                </span>
                {p.shared && (
                  <span
                    className="text-[9px] uppercase tracking-wider font-semibold flex-shrink-0"
                    style={{ color: c.secondaryText }}
                  >
                    Shared
                  </span>
                )}
              </button>
            );
          })}
        </div>
        <button
          onClick={() => {
            const name = window.prompt("Project name:");
            if (name && name.trim()) {
              const address = window.prompt("Address (optional):") || "";
              createProject({ name: name.trim(), address: address.trim() });
            }
          }}
          className="w-full flex items-center gap-3 px-3 py-2.5 mt-1 rounded-lg text-xs font-medium tracking-wide transition-colors"
          style={{ color: c.secondaryText }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = c.hoverBg;
            e.currentTarget.style.color = c.headingText;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = "transparent";
            e.currentTarget.style.color = c.secondaryText;
          }}
        >
          <Plus className="w-4 h-4" />
          Add Project
        </button>
      </div>

      {/* Spacer pushes footer to bottom */}
      <div className="flex-1" />

      <div
        className="p-4"
        style={{ borderTop: `1px solid ${c.border}` }}
      >
        <div
          className="text-[10px] uppercase tracking-wider font-semibold"
          style={{ color: c.secondaryText }}
        >
          Great Room & Kitchen Expansion
        </div>
        <div
          className="text-xs font-mono font-semibold mt-1"
          style={{ color: c.headingText }}
        >
          {roundSqFt(planBTotalSqFt)} ft²
        </div>

        {userEmail && (
          <div className="mt-4 pt-3" style={{ borderTop: `1px solid ${c.borderSoft}` }}>
            <p className="text-[10px] truncate" style={{ color: c.secondaryText }}>
              {userEmail}
            </p>
            <button
              onClick={onSignOut}
              className="mt-2 w-full flex items-center gap-2 px-2 py-1.5 rounded-lg text-[11px] transition-colors"
              style={{ color: c.bodyText }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = c.hoverBg;
                e.currentTarget.style.color = c.headingText;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = "transparent";
                e.currentTarget.style.color = c.bodyText;
              }}
            >
              <LogOut className="w-3 h-3" />
              Sign out
            </button>
          </div>
        )}
      </div>
    </>
  );

  const sidebarStyle = {
    background: c.sidebarGradient,
    borderRight: `1px solid ${c.sidebarBorder}`,
  };

  return (
    <>
      {/* Mobile overlay (hamburger lives in the global TopBar) */}
      {mobileOpen && (
        <div
          className="md:hidden fixed inset-0 z-40 bg-black/60 backdrop-blur-sm"
          onClick={() => setMobileOpen?.(false)}
        />
      )}

      {/* Desktop sidebar (always visible on md+)
          sticky + h-screen keeps the full sidebar (including sign-out footer)
          pinned to the viewport while the main content scrolls independently. */}
      <aside
        className="app-nav-sidebar hidden md:flex sticky top-0 h-screen flex-col flex-shrink-0 overflow-y-auto"
        style={{ ...sidebarStyle, width: `${sidebarWidth}px`, zIndex: 2147483646 }}
      >
        {sidebarContent}
        {/* Drag handle */}
        <div
          className="absolute top-0 right-0 w-1.5 h-full cursor-col-resize group z-10"
          onMouseDown={onDragStart}
          onTouchStart={onDragStart}
        >
          <div
            className="w-full h-full transition-colors group-hover:bg-blue-500/30 group-active:bg-blue-500/50"
          />
        </div>
      </aside>

      {/* Mobile sidebar (slide-in drawer) */}
      <aside
        className={`md:hidden fixed top-0 left-0 h-full w-64 z-50 flex flex-col transition-transform duration-300 ${
          mobileOpen ? "translate-x-0" : "-translate-x-full"
        }`}
        style={sidebarStyle}
      >
        <button
          onClick={() => setMobileOpen?.(false)}
          className="absolute top-3 right-3 p-1.5 rounded-lg"
          style={{
            background: "var(--bg-input)",
            border: "1px solid var(--border-input)",
          }}
          aria-label="Close menu"
        >
          <X className="w-3.5 h-3.5" style={{ color: "var(--text-secondary)" }} />
        </button>
        {sidebarContent}
      </aside>
    </>
  );
}
